import React from 'react';
import { Navigate } from 'react-router-dom';
import { useTheme } from '../hooks/useTheme';
import AuthContext from '../contexts/AuthContext';

/**
 * Admin page — manage the email registration blocklist.
 * Substring matches against the local part of the submitted email
 * during /api/auth/register. Hard block (generic 403).
 */
function AdminBlockedPatternsPage() {
  const auth = React.useContext(AuthContext);
  const { theme } = useTheme();

  const [rows, setRows] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [pattern, setPattern] = React.useState('');
  const [note, setNote] = React.useState('');
  const [saving, setSaving] = React.useState(false);
  const [msg, setMsg] = React.useState(null);

  const adminRoles = ['super_admin', 'company_admin', 'admin'];
  const isAdmin = auth.user && adminRoles.includes(auth.user.role);

  const load = React.useCallback(async () => {
    setLoading(true);
    try {
      const res = await auth.authFetch('/api/admin/blocked-patterns');
      if (!res.ok) throw new Error('Failed to load patterns');
      const data = await res.json();
      setRows(Array.isArray(data) ? data : []);
    } catch (err) {
      setMsg({ type: 'error', text: err.message });
    } finally {
      setLoading(false);
    }
  }, [auth]);

  React.useEffect(() => {
    if (auth.isAuthenticated && isAdmin) load();
  }, [auth.isAuthenticated, isAdmin, load]);

  if (!auth.isAuthenticated) return <Navigate to="/login" />;
  if (!isAdmin) {
    return (
      <div className="page-content">
        <h2>Blocked email patterns</h2>
        <p style={{ color: theme.textSecondary || '#666' }}>
          You need administrator access to view this page.
        </p>
      </div>
    );
  }

  const submit = async (e) => {
    e.preventDefault();
    setMsg(null);
    const normalized = pattern.toLowerCase().trim();
    if (!/^[a-z0-9._-]{2,40}$/.test(normalized)) {
      setMsg({ type: 'error', text: 'Pattern must be 2-40 chars of [a-z0-9._-]' });
      return;
    }
    setSaving(true);
    try {
      const res = await auth.authFetch('/api/admin/blocked-patterns', {
        method: 'POST',
        body: JSON.stringify({ pattern: normalized, note: note || null }),
      });
      const j = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(j.error || 'Failed to add pattern');
      setPattern('');
      setNote('');
      setMsg({ type: 'success', text: 'Pattern added.' });
      await load();
    } catch (err) {
      setMsg({ type: 'error', text: err.message });
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    if (!window.confirm('Remove this pattern? Registrations matching it will be allowed again.')) return;
    setMsg(null);
    try {
      const res = await auth.authFetch(`/api/admin/blocked-patterns/${id}`, { method: 'DELETE' });
      const j = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(j.error || 'Failed to remove pattern');
      setMsg({ type: 'success', text: 'Pattern removed.' });
      await load();
    } catch (err) {
      setMsg({ type: 'error', text: err.message });
    }
  };

  return (
    <div className="page-content">
      <h2>Blocked email patterns</h2>
      <p style={{ color: theme.textSecondary || '#666', marginBottom: '1.5rem' }}>
        Any registration whose email local part (before <code>@</code>) contains
        one of these patterns is rejected with a generic message. Substring
        match, case-insensitive.
      </p>

      {msg && (
        <div className={msg.type === 'error' ? 'alert alert-error' : 'alert alert-success'} style={{ marginBottom: '1rem' }}>
          {msg.text}
        </div>
      )}

      <div className="settings-section">
        <h3>Add pattern</h3>
        <form onSubmit={submit}>
          <div className="form-group">
            <label style={{ color: theme.textColor }}>Pattern</label>
            <input
              className="form-input"
              value={pattern}
              onChange={(e) => setPattern(e.target.value)}
              placeholder="e.g. charles"
              maxLength={40}
              required
            />
            <p style={{ color: theme.textSecondary || '#666', fontSize: '0.8rem', marginTop: '0.25rem' }}>
              Lowercase letters, digits, dot, dash, underscore. 2–40 characters.
            </p>
          </div>
          <div className="form-group">
            <label style={{ color: theme.textColor }}>Note (optional)</label>
            <input
              className="form-input"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="Why was this added?"
              maxLength={255}
            />
          </div>
          <button
            type="submit"
            className="btn btn-primary"
            style={{ backgroundColor: theme.accentColor }}
            disabled={saving}
          >
            {saving ? 'Adding...' : 'Add pattern'}
          </button>
        </form>
      </div>

      <div className="settings-section">
        <h3>Active patterns</h3>
        {loading ? (
          <p style={{ color: theme.textSecondary || '#666' }}>Loading...</p>
        ) : rows.length === 0 ? (
          <p style={{ color: theme.textSecondary || '#666' }}>No patterns configured.</p>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ borderBottom: `1px solid ${theme.borderColor || '#E5E5E5'}`, textAlign: 'left' }}>
                <th style={{ padding: '0.5rem 0.5rem 0.5rem 0' }}>Pattern</th>
                <th style={{ padding: '0.5rem' }}>Note</th>
                <th style={{ padding: '0.5rem' }}>Added</th>
                <th style={{ padding: '0.5rem', textAlign: 'right' }}></th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id} style={{ borderBottom: `1px solid ${theme.borderColor || '#F0F0F0'}` }}>
                  <td style={{ padding: '0.5rem 0.5rem 0.5rem 0', fontFamily: 'monospace' }}>{r.pattern}</td>
                  <td style={{ padding: '0.5rem', color: theme.textSecondary || '#666' }}>{r.note || ''}</td>
                  <td style={{ padding: '0.5rem', color: theme.textSecondary || '#666' }}>
                    {new Date(r.createdAt).toLocaleDateString()}
                  </td>
                  <td style={{ padding: '0.5rem', textAlign: 'right' }}>
                    <button
                      className="btn btn-secondary"
                      onClick={() => remove(r.id)}
                      style={{ color: theme.textColor }}
                    >
                      Remove
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

export default AdminBlockedPatternsPage;
